<template>
	<div class="l-seg" :class="{ 'l-seg--compact': compact }" role="group" :aria-label="ariaLabel || undefined">
		<button
			v-for="option in options"
			:key="option.Value"
			type="button"
			class="l-seg-opt no-tooltip-icon"
			:class="[{ 'is-on': option.Value === modelValue }, toneClass(option)]"
			:aria-pressed="option.Value === modelValue ? 'true' : 'false'"
			:title="option.Tip || null"
			:disabled="disabled || option.Disabled === true"
			@click.stop="emit('update:modelValue', option.Value)"
		>
			{{ option.Label }}
		</button>
	</div>
</template>










<script setup>
/**
 * §8.5 SegmentedControl: a small pill-style choice between a few options ("Side-by-side /
 * Change log", "Billable / Non-billable / Personal"). One option is on at a time.
 *
 * Promoted from the edit-timesheet2 page's local copy once the Approvals workspace needed it.
 * Use `v-model` with the option's `Value`. For choosing a time period use LPeriodSelector, and
 * for switching between pages of content use LTabs.
 */

//-- Props
defineProps({
	/**
	 * `[{ Value, Label, Tip, Disabled, Tone }]`. `Tone` ("success" | "danger") tints the option while it is on,
	 * for a choice whose answer carries meaning (Granted / Denied). Omit it for the plain pill.
	 */
	options: {
		type: Array,
		required: true,
	},

	modelValue: {
		type: String,
		default: '',
	},

	/** A tighter size, for use inside a panel. */
	compact: {
		type: Boolean,
		default: false,
	},

	/** Turn every option off at once (a save in flight, a locked row). */
	disabled: {
		type: Boolean,
		default: false,
	},

	/** What the group is, for a screen reader ("Variance view"). */
	ariaLabel: {
		type: String,
		default: '',
	},
});





//-- Emits
const emit = defineEmits(['update:modelValue']);





//-- Methods
const toneClass = (option) => (option.Tone ? 'l-seg-opt--' + option.Tone : '');
</script>










<style scoped>
.l-seg
{
	display: flex;
	gap: 3px;
	padding: 3px;
	background: var(--surface-sunken);
	border: 1px solid var(--line);
	border-radius: var(--r);
}

.l-seg-opt
{
	flex: 1;
	text-align: center;
	white-space: nowrap;
	min-height: var(--target-min);
	border: 0;
	background: transparent;
	border-radius: var(--r-sm);
	padding: 6px 10px;
	font-family: var(--font-ui);
	font-size: var(--text-chip);
	font-weight: 700;
	color: var(--text-2);
	cursor: pointer;
	transition: background var(--t-fast) var(--ease), color var(--t-fast) var(--ease);
}

.l-seg-opt:hover:not(:disabled)
{
	color: var(--text);
}

.l-seg-opt.is-on
{
	background: var(--surface);
	color: var(--text);
	box-shadow: var(--shadow-sm);
}

.l-seg-opt--success.is-on
{
	background: var(--status-approved-fill);
	color: var(--status-approved-text);
}

.l-seg-opt--danger.is-on
{
	background: var(--status-rejected-fill);
	color: var(--status-rejected-text);
}

.l-seg-opt:disabled
{
	color: var(--text-3);
	opacity: 0.55;
	cursor: not-allowed;
}

.l-seg--compact .l-seg-opt
{
	flex: none;
	padding: 4px 11px;
	font-size: var(--text-micro);
}
</style>
